import { useState, useEffect } from "react";
import { Dialog, Checkbox, Button, Row, TextInput } from "@narmi/design_system";
import { $staffApi } from "../../api";
import { useUserConfiguration } from "../../hooks/useUserConfiguration.ts";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { useForm, useFormField } from "@hooks/useForm";
import { StaffApiSchema } from "@apiUtils/index";
import styles from "./SurveyPopup.module.css";

type Survey = StaffApiSchema.components["schemas"]["Survey"];

const SurveyPopup = () => {
  const { sendNotification } = useNotificationContext();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [impressionUuid, setImpressionUuid] = useState("");
  const { currentUser, roles } = useUserConfiguration();

  const { data: surveyQuestionsResponse } = $staffApi.useQuery(
    "get",
    "/api/survey_questions/",
    {
      params: {
        query: { survey_uuid: survey?.uuid },
      },
    },
    {
      // No refetching because an impression is created immediately when the survey is opened
      // Calling this endpoint again would reflect that the user saw the survey
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: !!survey && !roles.narmi, // Narmi users do not see surveys
    }
  );

  const surveyQuestions = surveyQuestionsResponse?.results || [];

  // Initially, choicesQuestion will always be the Nps Survey question
  const choicesQuestion = surveyQuestions.find(
    (surveyQuestion) => surveyQuestion.question_detail.answer_content_type === "npsscore"
  )?.question_detail;

  const freeTextQuestions = surveyQuestions.filter(
    (surveyQuestion) => surveyQuestion.question_detail.answer_content_type === "freeformtext"
  );

  const { mutate: createSurveyImpression } = $staffApi.useMutation(
    "post",
    "/api/survey_impressions/",
    {
      onSuccess: (data) => {
        setImpressionUuid(data.uuid);
        setIsModalOpen(true);
      },
      onError: () => {
        sendNotification({
          type: "negative",
          text: "Something went wrong.",
        });
        setIsModalOpen(false);
      },
    }
  );

  const { mutateAsync: createAnswer } = $staffApi.useMutation(
    "post",
    "/api/survey_impressions/create_answer/",
    {
      onError: () => {
        sendNotification({
          type: "negative",
          text: "Something went wrong while submitting your response.",
        });
        setIsModalOpen(false);
      },
    }
  );

  const formChoice = useFormField(-1, {
    validator: (value) => (choicesQuestion?.is_required && value < 0 ? "Required." : null),
  });

  const choiceForm = useForm(
    { formChoice },
    {
      onSubmit: () => {
        createAnswer({
          body: {
            impression_uuid: impressionUuid,
            question_uuid: choicesQuestion?.uuid || "",
            value: formChoice.value,
          },
        });
      },
    }
  );

  const freeTextAnswer1 = useFormField("", {
    validator: (value) =>
      !value && freeTextQuestions[0]?.question_detail.is_required ? "Required." : null,
  });
  const freeTextAnswer2 = useFormField("", {
    validator: (value) =>
      !value && freeTextQuestions[1]?.question_detail.is_required ? "Required." : null,
  });
  const freeTextAnswer3 = useFormField("", {
    validator: (value) =>
      !value && freeTextQuestions[2]?.question_detail.is_required ? "Required." : null,
  });
  const freeTextAnswers = [freeTextAnswer1, freeTextAnswer2, freeTextAnswer3];

  // Surveys support up to 3 questions in total, meaning if there is a choicesQuestion,
  // there can only be 2 free text questions.
  const thirdFreeTextAnswerOrEmpty: { freeTextAnswer3?: typeof freeTextAnswer3 } = choicesQuestion
    ? {}
    : { freeTextAnswer3 };

  const freeTextForm = useForm(
    { freeTextAnswer1, freeTextAnswer2, ...thirdFreeTextAnswerOrEmpty },
    {
      parent: choicesQuestion ? choiceForm : undefined,
      onSubmit: async () => {
        if (freeTextAnswer1.value) {
          await createAnswer({
            body: {
              impression_uuid: impressionUuid,
              question_uuid: freeTextQuestions[0].question_detail.uuid!,
              value: freeTextAnswer1.value,
            },
          });
        }
        if (freeTextAnswer2.value) {
          await createAnswer({
            body: {
              impression_uuid: impressionUuid,
              question_uuid: freeTextQuestions[1].question_detail.uuid!,
              value: freeTextAnswer2.value,
            },
          });
        }
        if (freeTextQuestions.length === 3 && freeTextAnswer3.value) {
          await createAnswer({
            body: {
              impression_uuid: impressionUuid,
              question_uuid: freeTextQuestions[2].question_detail.uuid!,
              value: freeTextAnswer3.value,
            },
          });
        }
        sendNotification({
          type: "success",
          text: "Responses submitted successfully.",
        });
      },
    }
  );

  useEffect(() => {
    // Narmi users submitting survey responses is not supported
    if (!currentUser.id || roles.narmi) {
      return;
    }

    // fetch is used over react query to prevent auto refetching. The api response
    // is not idempotent since an impression is created immediately after the
    // survey is fetched (or in the user's case, when they first see the popup).
    const fetchPendingSurveys = async () => {
      const response = await fetch(
        `/api/survey/?${new URLSearchParams({
          limit: "1",
          offset: "0",
          pending_response: "true",
        }).toString()}`
      );

      if (!response.ok) {
        sendNotification({
          type: "negative",
          text: "Something went wrong when fetching surveys.",
        });
        return;
      }

      const data = await response.json();
      if (!data.results.length) {
        return;
      }

      const surveyData = data.results[0];
      setSurvey(surveyData);
      setIsModalOpen(true);

      // to ensure only one impression is created per time the user sees the survey
      if (!impressionUuid) {
        createSurveyImpression({
          body: {
            survey: surveyData.uuid || "",
          },
        });
      }
    };
    fetchPendingSurveys();
  }, [createSurveyImpression, currentUser.id, roles, impressionUuid, sendNotification]);

  return (
    <Dialog
      isOpen={isModalOpen && (!!choicesQuestion || freeTextQuestions.length > 0)}
      title="Thanks for being a Narmi customer!"
      onUserDismiss={() => setIsModalOpen(false)}
    >
      {choicesQuestion && !freeTextForm.isEnabled ? (
        <>
          <div className="margin--y--m">{choicesQuestion.text}</div>{" "}
          <Row alignItems="center" justifyContent="space-between">
            {choicesQuestion?.answer_choices_values?.map((choice, i) => (
              <Row.Item className="padding--top--xs" key={`nps_score-${i}`}>
                <Checkbox
                  name={`nps_score-${i}`}
                  value={(choice as number).toString()}
                  checked={formChoice.value === choice}
                  onChange={(e) => {
                    formChoice.setValue(parseInt(e.target.value));
                  }}
                />
                <div className={`padding--x--${choice === 10 ? "xxxs" : "xxs"} padding--y--xs`}>
                  {(choice as number).toString()}
                </div>
              </Row.Item>
            ))}
          </Row>
          <Row className={styles.likelyText}>
            <Row.Item>Least likely</Row.Item>
            <Row.Item className="textAlign--right" shrink>
              Most likely
            </Row.Item>
          </Row>
          <Row className="margin--top--l" justifyContent="end">
            <Row.Item shrink>
              <Button
                label="Submit"
                onClick={() => {
                  choiceForm.submit();
                  if (freeTextQuestions.length < 1) {
                    setIsModalOpen(false);
                    sendNotification({
                      type: "success",
                      text: "Response submitted successfully.",
                    });
                  }
                }}
                disabled={!choiceForm.isValid()}
              />
            </Row.Item>
          </Row>
        </>
      ) : null}
      {freeTextForm.isEnabled ? (
        <>
          {freeTextQuestions.map((surveyQuestion, i) => (
            <div key={surveyQuestion.uuid}>
              <div className="margin--y--m">{surveyQuestion.question_detail.text}</div>
              <TextInput
                multiline
                value={freeTextAnswers[i].value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  freeTextAnswers[i].setValue(e.target.value)
                }
                error={freeTextAnswers[i].error}
              />
            </div>
          ))}

          <Row className="margin--top--l" justifyContent="end">
            <Row.Item shrink>
              <Button
                label="Submit"
                onClick={() => {
                  freeTextForm.submit();
                  setIsModalOpen(false);
                }}
                disabled={!freeTextForm.isValid()}
              />
            </Row.Item>
          </Row>
        </>
      ) : null}
    </Dialog>
  );
};

export default SurveyPopup;
